import type { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import type { RoofEdgeHandle, RoofHandle } from "building-types/handles/docEntityHandleTypes";
import type { RoofEdge } from "building-types/roofs/roofEdgeTypes";
import type { RoofBlocks } from "building-types/roofs/roofSystemTypes";
import { isIndexedRoofPeakDirection } from "building-types/roofs/roofSystemTypes";
import { RoofWallFramingType } from "building-types/roofs/roofWallFramingTypes";
import type { GeneratedRoofVolumeLegacy } from "building-types/roofVolumes/roofVolumeTypes";
import type { RoofFramingSettings } from "building-types/settings/roofFramingSettings";
import type { RoofSettings } from "building-types/settings/roofSettings";
import type {
  EvaluatedRoofEdgeOverrides,
  EvaluatedRoofEdgeOverridesTable,
  EvaluatedRoofSideOverridesTable,
  RectangularOverhangs,
  RoofEdgeOverrides,
  RoofExtensions,
  RoofHeelHeightOffsets,
  RoofRails2D,
  RoofSlope,
  ShedRoofExtensions,
} from "building-types/settings/roofTypes";
import { RoofSide } from "building-types/settings/roofTypes";
import { type EaveType, type EaveCutType, RoofPeakDirection } from "building-types/settings/settingsTypesEnum";
import { GableRoofType } from "building-types/settings/settingsTypesEnum";
import type { Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { isFormula } from "doc/formula/formulaParsing";
import { isVirtualRoofBlock } from "doc/roofSystemTools";
import { roofRails2DNew } from "doc/utils/roofTools";
import { Line2Side, DIST_EPSILON } from "geom/core";
import { intersectLine2Line2 } from "geom/intersectLine2Line2";
import { line2IsParallel, line2Side, line2PerpDistance } from "geom/line2";
import { offsetPolyLine2 } from "geom/offset";
import { planeNew } from "geom/plane";
import {
  polyLine2EdgeCount,
  polyLine2Edge,
  polyLine2EdgeTangent,
  polyLine2ByTwoPoints,
  polyLine2Extend,
  polyLine2Start,
  polyLine2End,
  polyLine2EdgeStart,
  polyLine2EdgeEnd,
  polyLine2Morph,
  polyLine2Copy,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3EdgeTangent } from "geom/polyLine3";
import { vec2Dot, vec2Perp } from "geom/vec2";
import { includeElementBasedOnOrientation, isValidRoofTypeForEdgeOverrides } from "../multiGableRoofTools";
import { roofEdgeNew } from "./roofEdgeTypes";
import { resolveRoofWallFramingType, resolvedRoofMaterialTrimAndProps } from "./roofSideOverridesUtility";
import { getFirstSlope, getLastSlope, isRoofEdgeAlignedToSide } from "./roofSlopes";

export interface BuildRoofEdgesOptions {
  readonly boundingPerimeter: PolyLine2;
  readonly offsetBoundingPerimeter: PolyLine2;
  readonly exactPerimeter: PolyLine2;
  readonly offsetExactPerimeter: PolyLine2;
  readonly startEdge: number;
  readonly subRoofId: string;
  readonly baseHeight: number;
  readonly leftSpan: number;
  readonly rightSpan: number;
  readonly heelHeightOffsets: RoofHeelHeightOffsets;
  readonly rectangularOverhangs: RectangularOverhangs;
  readonly edgeOverhangs: number[];
  readonly edgeExteriorWallOffsetWithFinish: number[];
  readonly edgeExteriorWallOffsetFromOverhangDatum: number[];
  readonly eaveSlope: number;
  readonly eaveExtensionAtCoplanarWall: number;
  readonly eaveExtensionAtNonCoplanarRake: number;
  readonly leftSideSlopes: RoofSlope[];
  readonly rightSideSlopes: RoofSlope[];
  readonly startSlopes: RoofSlope[];
  readonly endSlopes: RoofSlope[];
  readonly peakRail: PolyLine2;
  readonly fullyTruncatedStart: boolean;
  readonly fullyTruncatedEnd: boolean;
  readonly eaveType: EaveType;
  readonly eaveCutType: EaveCutType;
  readonly extensions: RoofExtensions;
  readonly shedExtensions: ShedRoofExtensions;
  readonly roofType?: GableRoofType;
  readonly insertedRoofVolumes?: Table<GeneratedRoofVolumeLegacy>;
  readonly roofEdgeOverrides?: EvaluatedRoofEdgeOverridesTable;
  readonly roofSideOverrides?: EvaluatedRoofSideOverridesTable;
  readonly roofSettings: RoofSettings;
  readonly roofFramingSettings: RoofFramingSettings;
}

export function buildRoofEdges({
  boundingPerimeter,
  offsetBoundingPerimeter,
  startEdge,
  subRoofId,
  baseHeight,
  rectangularOverhangs,
  edgeExteriorWallOffsetWithFinish,
  edgeExteriorWallOffsetFromOverhangDatum,
  eaveExtensionAtCoplanarWall,
  eaveExtensionAtNonCoplanarRake,
  eaveSlope,
  leftSideSlopes,
  rightSideSlopes,
  startSlopes,
  endSlopes,
  heelHeightOffsets,
  fullyTruncatedStart,
  fullyTruncatedEnd,
  eaveType,
  eaveCutType,
  roofType = GableRoofType.DEFAULT,
  roofSideOverrides,
  roofSettings,
  roofFramingSettings,
}: BuildRoofEdgesOptions): RoofEdge[] {
  const edges: RoofEdge[] = new Array<RoofEdge>(polyLine2EdgeCount(boundingPerimeter));

  const { leftOffset: leftHeightOffset, rightOffset: rightHeightOffset } = heelHeightOffsets;
  const { startOverhang, endOverhang, leftOverhang, rightOverhang } = rectangularOverhangs;

  const startEdgePoly = polyLine2Edge(boundingPerimeter, startEdge);
  if (!roofSideOverrides) {
    return [];
  }
  const startSideOverrides = roofSideOverrides[RoofSide.START] ?? {};
  const startEdgeRoofMaterialTrimAndProps = resolvedRoofMaterialTrimAndProps(
    startEdgePoly,
    startSideOverrides,
    roofSettings,
    !fullyTruncatedStart,
    RoofSide.START,
    fullyTruncatedStart,
  );

  edges[startEdge] = roofEdgeNew({
    subRoofId,
    edge: startEdgePoly,
    outerEdge: polyLine2Edge(offsetBoundingPerimeter, startEdge),
    startHeight: baseHeight,
    endHeight: baseHeight,
    slopes: fullyTruncatedStart ? startSlopes : [],
    truncatedSlopes: startSlopes,
    eaveSlope: fullyTruncatedStart ? eaveSlope : Number.POSITIVE_INFINITY,
    eaveExtensionAtCoplanarWall,
    eaveExtensionAtNonCoplanarRake,
    eaveType,
    eaveCutType: startSideOverrides.eaveCutType ?? eaveCutType,
    exteriorWallOffsetWithFinish: edgeExteriorWallOffsetWithFinish[startEdge],
    exteriorWallOffsetFromOverhangDatum: edgeExteriorWallOffsetFromOverhangDatum[startEdge],
    sideOverhang: endOverhang,
    startOverhang: leftOverhang,
    endOverhang: rightOverhang,
    side: RoofSide.START,
    isStartEdge: true,
    isRake: !fullyTruncatedStart,
    fullyTruncated: fullyTruncatedStart,
    roofMaterialTrimAndProps: startEdgeRoofMaterialTrimAndProps,
    roofEdgeOverrides: startSideOverrides,
    framingType: resolveRoofWallFramingType(roofFramingSettings, startSideOverrides, RoofSide.START),
  });

  const indexRightEdge = (startEdge + 1) % edges.length;
  const rightEdgePoly = polyLine2Edge(boundingPerimeter, indexRightEdge);
  const rightSideOverrides = roofSideOverrides[RoofSide.RIGHT] ?? {};
  const rightEdgeRoofMaterialTrimAndProps = resolvedRoofMaterialTrimAndProps(
    rightEdgePoly,
    rightSideOverrides,
    roofSettings,
    false,
    RoofSide.RIGHT,
    false,
  );

  edges[indexRightEdge] = roofEdgeNew({
    subRoofId,
    edge: rightEdgePoly,
    outerEdge: polyLine2Edge(offsetBoundingPerimeter, indexRightEdge),
    startHeight: baseHeight + rightHeightOffset,
    endHeight: baseHeight + rightHeightOffset,
    heightOffset: roofType === GableRoofType.LEFT_SIDE_SHED_ROOF ? 0 : rightHeightOffset,
    slopes: roofType === GableRoofType.LEFT_SIDE_SHED_ROOF ? [] : rightSideSlopes,
    oppSlopes: roofType === GableRoofType.DEFAULT ? leftSideSlopes : [],
    eaveSlope: roofType === GableRoofType.LEFT_SIDE_SHED_ROOF ? Number.POSITIVE_INFINITY : eaveSlope,
    eaveExtensionAtCoplanarWall,
    eaveExtensionAtNonCoplanarRake,
    eaveType,
    eaveCutType: rightSideOverrides.eaveCutType ?? eaveCutType,
    exteriorWallOffsetWithFinish: edgeExteriorWallOffsetWithFinish[indexRightEdge],
    exteriorWallOffsetFromOverhangDatum: edgeExteriorWallOffsetFromOverhangDatum[indexRightEdge],
    sideOverhang: rightOverhang,
    startOverhang: startOverhang,
    endOverhang: endOverhang,
    side: RoofSide.RIGHT,
    isRightEdge: true,
    isRake: false,
    roofMaterialTrimAndProps: rightEdgeRoofMaterialTrimAndProps,
    roofEdgeOverrides: rightSideOverrides,
    framingType: resolveRoofWallFramingType(roofFramingSettings, rightSideOverrides, RoofSide.RIGHT),
  });

  const indexEndEdge = (startEdge + 2) % edges.length;
  const endEdgePoly = polyLine2Edge(boundingPerimeter, indexEndEdge);
  const endSideOverrides = roofSideOverrides[RoofSide.END] ?? {};
  const endEdgeRoofMaterialTrimAndProps = resolvedRoofMaterialTrimAndProps(
    endEdgePoly,
    endSideOverrides,
    roofSettings,
    !fullyTruncatedEnd,
    RoofSide.END,
    false,
  );

  edges[indexEndEdge] = roofEdgeNew({
    subRoofId,
    edge: endEdgePoly,
    outerEdge: polyLine2Edge(offsetBoundingPerimeter, indexEndEdge),
    startHeight: baseHeight,
    endHeight: baseHeight,
    slopes: fullyTruncatedEnd ? endSlopes : [],
    truncatedSlopes: endSlopes,
    eaveSlope: fullyTruncatedEnd ? eaveSlope : Number.POSITIVE_INFINITY,
    eaveExtensionAtCoplanarWall,
    eaveExtensionAtNonCoplanarRake,
    eaveType,
    eaveCutType: endSideOverrides.eaveCutType ?? eaveCutType,
    exteriorWallOffsetWithFinish: edgeExteriorWallOffsetWithFinish[indexEndEdge],
    exteriorWallOffsetFromOverhangDatum: edgeExteriorWallOffsetFromOverhangDatum[indexEndEdge],
    sideOverhang: endOverhang,
    startOverhang: rightOverhang,
    endOverhang: leftOverhang,
    isEndEdge: true,
    isRake: !fullyTruncatedEnd,
    side: RoofSide.END,
    fullyTruncated: fullyTruncatedEnd,
    roofMaterialTrimAndProps: endEdgeRoofMaterialTrimAndProps,
    roofEdgeOverrides: endSideOverrides,
    framingType: resolveRoofWallFramingType(roofFramingSettings, endSideOverrides, RoofSide.END),
  });

  const indexLeftEdge = (startEdge + 3) % edges.length;
  const leftEdgePoly = polyLine2Edge(boundingPerimeter, indexLeftEdge);
  const leftSideOverrides = roofSideOverrides[RoofSide.LEFT] ?? {};
  const leftEdgeRoofMaterialTrimAndProps = resolvedRoofMaterialTrimAndProps(
    leftEdgePoly,
    leftSideOverrides,
    roofSettings,
    false,
    RoofSide.LEFT,
    false,
  );

  edges[indexLeftEdge] = roofEdgeNew({
    subRoofId,
    edge: leftEdgePoly,
    outerEdge: polyLine2Edge(offsetBoundingPerimeter, indexLeftEdge),
    startHeight: baseHeight + leftHeightOffset,
    endHeight: baseHeight + leftHeightOffset,
    heightOffset: roofType === GableRoofType.RIGHT_SIDE_SHED_ROOF ? 0 : leftHeightOffset,
    slopes: roofType === GableRoofType.RIGHT_SIDE_SHED_ROOF ? [] : leftSideSlopes,
    oppSlopes: roofType === GableRoofType.DEFAULT ? rightSideSlopes : [],
    eaveSlope: roofType === GableRoofType.RIGHT_SIDE_SHED_ROOF ? Number.POSITIVE_INFINITY : eaveSlope,
    eaveExtensionAtCoplanarWall,
    eaveExtensionAtNonCoplanarRake,
    eaveType,
    eaveCutType: leftSideOverrides.eaveCutType ?? eaveCutType,
    exteriorWallOffsetWithFinish: edgeExteriorWallOffsetWithFinish[indexLeftEdge],
    exteriorWallOffsetFromOverhangDatum: edgeExteriorWallOffsetFromOverhangDatum[indexLeftEdge],
    sideOverhang: leftOverhang,
    startOverhang: startOverhang,
    endOverhang: endOverhang,
    side: RoofSide.LEFT,
    isLeftEdge: true,
    isRake: false,
    roofMaterialTrimAndProps: leftEdgeRoofMaterialTrimAndProps,
    roofEdgeOverrides: leftSideOverrides,
    framingType: resolveRoofWallFramingType(roofFramingSettings, leftSideOverrides, RoofSide.LEFT),
  });

  return edges;
}

export function buildExactRoofEdges({
  boundingPerimeter,
  exactPerimeter,
  offsetExactPerimeter,
  startEdge,
  subRoofId,
  baseHeight,
  leftSpan,
  rightSpan,
  heelHeightOffsets,
  rectangularOverhangs,
  edgeOverhangs,
  edgeExteriorWallOffsetWithFinish,
  edgeExteriorWallOffsetFromOverhangDatum,
  eaveSlope,
  leftSideSlopes,
  rightSideSlopes,
  startSlopes,
  endSlopes,
  peakRail,
  fullyTruncatedStart,
  fullyTruncatedEnd,
  extensions,
  shedExtensions,
  eaveType,
  eaveCutType,
  eaveExtensionAtCoplanarWall,
  eaveExtensionAtNonCoplanarRake,
  roofEdgeOverrides,
  roofSideOverrides,
  roofType = GableRoofType.DEFAULT,
  insertedRoofVolumes,
  roofSettings,
  roofFramingSettings,
}: BuildRoofEdgesOptions): RoofEdge[] {
  const exactEdges: RoofEdge[] = [];

  const p0x = peakRail[0];
  const p0y = peakRail[1];
  const p1x = peakRail[2];
  const p1y = peakRail[3];

  const [dirX, dirY] = polyLine2EdgeTangent(peakRail, 0);

  const { leftOffset: leftHeightOffset, rightOffset: rightHeightOffset } = heelHeightOffsets;
  const { leftOverhang, rightOverhang } = rectangularOverhangs;
  const { hasStartExtension, hasEndExtension } = extensions;
  const { hasLeftExtension, hasRightExtension } = shedExtensions;

  const sideAlignedParam = {
    boundingPerimeter,
    exactPerimeter,
    startEdge,
  };

  const findEdgeAlignedRoofId = (
    ax: number,
    ay: number,
    bx: number,
    by: number,
    nX: number,
    nY: number,
    isStart: boolean,
  ): string | undefined => {
    if (!insertedRoofVolumes) {
      return undefined;
    }

    for (const hostRoofId in insertedRoofVolumes) {
      const hostRoof = insertedRoofVolumes[hostRoofId];

      const { rails: hostRails, eaveEdges: hostEaveEdges } = hostRoof;

      const { leftRail: hostLeftRail, rightRail: hostRightRail, peakRail: hostPeakRail } = hostRails;

      const idx = isStart ? 0 : 3;

      const [hostNormalX, hostNormalY] = polyLine3EdgeTangent(hostPeakRail, 0, isStart);

      let hostEndRail = polyLine2ByTwoPoints(
        hostLeftRail[idx],
        hostLeftRail[idx + 1],
        hostRightRail[idx],
        hostRightRail[idx + 1],
      );

      hostEndRail = offsetPolyLine2(hostEndRail, isStart ? -leftOverhang : rightOverhang);
      hostEndRail = polyLine2Extend(hostEndRail, 1, 1);

      const [x0, y0] = polyLine2Start(hostEndRail);
      const [x1, y1] = polyLine2End(hostEndRail);

      if (line2IsParallel(x0, y0, x1, y1, p0x, p0y, p1x, p1y) && vec2Dot(hostNormalX, hostNormalY, nX, nY) > 0) {
        if (intersectLine2Line2(x0, y0, x1, y1, ax, ay, bx, by) !== null) {
          return hostRoof.id;
        }
      } else {
        for (const { edge, endAlignedHostRoofId, startAlignedHostRoofId } of hostEaveEdges) {
          const otherAlignedHostRoofId = isStart ? startAlignedHostRoofId : endAlignedHostRoofId;

          const [ex0, ey0] = polyLine2Start(edge);
          const [ex1, ey1] = polyLine2End(edge);
          if (
            line2IsParallel(ex0, ey0, ex1, ey1, ax, ay, bx, by) &&
            intersectLine2Line2(ex0, ey0, ex1, ey1, ax, ay, bx, by) !== null
          ) {
            return otherAlignedHostRoofId;
          }
        }
      }
    }

    return undefined;
  };

  const leftRail = polyLine2Edge(boundingPerimeter, startEdge + 3);
  const rightRail = polyLine2Edge(boundingPerimeter, startEdge + 1);

  const l0x = leftRail[0];
  const l0y = leftRail[1];
  const l1x = leftRail[2];
  const l1y = leftRail[3];

  const r0x = rightRail[0];
  const r0y = rightRail[1];
  const r1x = rightRail[2];
  const r1y = rightRail[3];

  const { slope: firstLeftSlope } = getFirstSlope(leftSideSlopes);
  const { slope: firstRightSlope } = getFirstSlope(rightSideSlopes);
  const { slope: lastLeftSlope } = getLastSlope(leftSideSlopes);
  const { slope: lastRightSlope } = getLastSlope(rightSideSlopes);

  const getStartEndHeight = (ax: number, ay: number, bx: number, by: number): [number, number] => {
    const startSide = line2Side(p0x, p0y, p1x, p1y, ax, ay);
    const endSide = line2Side(p0x, p0y, p1x, p1y, bx, by);

    const startHeightOffset = startSide === Line2Side.RIGHT ? rightHeightOffset : leftHeightOffset;
    const endHeightOffset = endSide === Line2Side.RIGHT ? rightHeightOffset : leftHeightOffset;

    const startSpan = startSide === Line2Side.RIGHT ? rightSpan : leftSpan;
    const endSpan = endSide === Line2Side.RIGHT ? rightSpan : leftSpan;

    const startOverhang = startSide === Line2Side.RIGHT ? rightOverhang : leftOverhang;
    const endOverhang = endSide === Line2Side.RIGHT ? rightOverhang : leftOverhang;

    const startFirstSlope = startSide === Line2Side.RIGHT ? firstRightSlope : firstLeftSlope;
    const endFirstSlope = endSide === Line2Side.RIGHT ? firstRightSlope : firstLeftSlope;

    const startPerpDist = line2PerpDistance(p0x, p0y, p1x, p1y, ax, ay);
    const endPerpDist = line2PerpDistance(p0x, p0y, p1x, p1y, bx, by);

    const startDist = startPerpDist + startOverhang;
    const endDist = endPerpDist + endOverhang;

    const startHeight = baseHeight + startHeightOffset + (startSpan - startDist) * startFirstSlope;
    const endHeight = baseHeight + endHeightOffset + (endSpan - endDist) * endFirstSlope;

    //The following code is commented for now as it seems to be creating too many diffs with
    //the old trim generation option.
    // if (roofType === GableRoofType.DEFAULT) {
    //   startHeight = baseHeight + startHeightOffset + (startSpan - startDist) * startFirstSlope;
    //   endHeight = baseHeight + endHeightOffset + (endSpan - endDist) * endFirstSlope;
    // } else {
    //   if (startPerpDist > endPerpDist) {
    //     startHeight = baseHeight + startHeightOffset + (startSpan - startDist) * startFirstSlope;
    //     endHeight = baseHeight + startHeightOffset + (startSpan + endDist - wallThicknessOffset) * startLastSlope;
    //   } else {
    //     startHeight = baseHeight + endHeightOffset + (startSpan + startDist - wallThicknessOffset) * startLastSlope;
    //     endHeight = baseHeight + endHeightOffset + (startSpan - endDist) * startFirstSlope;
    //   }
    // }
    return [startHeight, endHeight];
  };

  if (!roofEdgeOverrides || !roofSideOverrides) {
    return [];
  }

  for (let i = 0, l = polyLine2EdgeCount(exactPerimeter); i < l; i++) {
    const { slope: customHipSlope = Number.POSITIVE_INFINITY } = roofEdgeOverrides[i] ?? {};

    if (isFormula(customHipSlope)) {
      throw new Error("Custom hip slope is a formula");
    }

    const [ax, ay] = polyLine2EdgeStart(exactPerimeter, i);
    const [bx, by] = polyLine2EdgeEnd(exactPerimeter, i);

    let sideOverride: EvaluatedRoofEdgeOverrides = {};

    if (isRoofEdgeAlignedToSide({ ...sideAlignedParam, side: RoofSide.START, edgeIndex: i })) {
      sideOverride = roofSideOverrides[RoofSide.START] ?? {};
    } else if (isRoofEdgeAlignedToSide({ ...sideAlignedParam, side: RoofSide.END, edgeIndex: i })) {
      sideOverride = roofSideOverrides[RoofSide.END] ?? {};
    } else if (isRoofEdgeAlignedToSide({ ...sideAlignedParam, side: RoofSide.LEFT, edgeIndex: i })) {
      sideOverride = roofSideOverrides[RoofSide.LEFT] ?? {};
    } else if (isRoofEdgeAlignedToSide({ ...sideAlignedParam, side: RoofSide.RIGHT, edgeIndex: i })) {
      sideOverride = roofSideOverrides[RoofSide.RIGHT] ?? {};
    }
    let resolvedEdgeOverride = { ...sideOverride };
    if (roofEdgeOverrides[i]) {
      resolvedEdgeOverride = { ...resolvedEdgeOverride, ...roofEdgeOverrides[i] };
    }

    if (line2IsParallel(p0x, p0y, p1x, p1y, ax, ay, bx, by)) {
      const side = line2Side(p0x, p0y, p1x, p1y, ax, ay);
      const distL = line2PerpDistance(p0x, p0y, p1x, p1y, ax, ay) + leftOverhang;
      const distR = line2PerpDistance(p0x, p0y, p1x, p1y, ax, ay) + rightOverhang;
      const leftDist = line2PerpDistance(l0x, l0y, l1x, l1y, ax, ay);
      const rightDist = line2PerpDistance(r0x, r0y, r1x, r1y, ax, ay);
      const isShedHalfEdge =
        (roofType === GableRoofType.LEFT_SIDE_SHED_ROOF && rightDist < DIST_EPSILON) ||
        (roofType === GableRoofType.RIGHT_SIDE_SHED_ROOF && leftDist < DIST_EPSILON);

      if (side === Line2Side.LEFT && rightDist > -DIST_EPSILON) {
        const [nX, nY] = vec2Perp(-dirX, -dirY);
        const startAlignedHostRoofId = isShedHalfEdge ? undefined : findEdgeAlignedRoofId(ax, ay, bx, by, nX, nY, true);
        const endAlignedHostRoofId = isShedHalfEdge ? undefined : findEdgeAlignedRoofId(ax, ay, bx, by, nX, nY, false);
        let height = baseHeight + leftHeightOffset + (leftSpan - distL) * firstLeftSlope;
        const isRake = Math.abs(leftDist) > DIST_EPSILON && !isShedHalfEdge;
        // extension edges need to be classified as INVALID so the roofing material extrusion is also extended
        let side = RoofSide.LEFT;
        const resolvedMaterialTrimAndProps = resolvedRoofMaterialTrimAndProps(
          polyLine2Edge(exactPerimeter, i),
          resolvedEdgeOverride,
          roofSettings,
          isRake,
          side,
          false,
        );
        const resolvedFramingType = resolveRoofWallFramingType(roofFramingSettings, resolvedEdgeOverride, side);

        if (isShedHalfEdge) {
          side = hasRightExtension ? RoofSide.INVALID : RoofSide.RIGHT;
          height = baseHeight + leftHeightOffset + (leftSpan + distL) * lastLeftSlope;
        }

        exactEdges.push(
          roofEdgeNew({
            subRoofId,
            edge: polyLine2Edge(exactPerimeter, i),
            outerEdge: polyLine2Edge(offsetExactPerimeter, i),
            startHeight: height,
            endHeight: height,
            heightOffset: leftHeightOffset,
            slopes: roofType === GableRoofType.RIGHT_SIDE_SHED_ROOF ? [] : leftSideSlopes,
            oppSlopes: roofType === GableRoofType.DEFAULT ? rightSideSlopes : [],
            eaveSlope:
              roofType === GableRoofType.RIGHT_SIDE_SHED_ROOF || Math.abs(rightDist) < DIST_EPSILON
                ? Number.POSITIVE_INFINITY
                : eaveSlope,
            eaveExtensionAtCoplanarWall,
            eaveExtensionAtNonCoplanarRake,
            eaveType,
            eaveCutType: resolvedEdgeOverride.eaveCutType ?? eaveCutType,
            exteriorWallOffsetWithFinish: edgeExteriorWallOffsetWithFinish[i],
            exteriorWallOffsetFromOverhangDatum: edgeExteriorWallOffsetFromOverhangDatum[i],
            sideOverhang: edgeOverhangs[i],
            startOverhang: edgeOverhangs[(i + l - 1) % l],
            endOverhang: edgeOverhangs[(i + 1) % l],
            side,
            isLeftEdge: Math.abs(leftDist) < DIST_EPSILON,
            isRightEdge: Math.abs(rightDist) < DIST_EPSILON,
            isRake,
            startAlignedHostRoofId,
            endAlignedHostRoofId,
            roofMaterialTrimAndProps: resolvedMaterialTrimAndProps,
            roofEdgeOverrides: resolvedEdgeOverride,
            isShedHalfEdge,
            framingType: resolvedFramingType,
          }),
        );
      } else if (side === Line2Side.RIGHT && leftDist > -DIST_EPSILON) {
        const [nX, nY] = vec2Perp(dirX, dirY);
        const startAlignedHostRoofId = isShedHalfEdge ? undefined : findEdgeAlignedRoofId(ax, ay, bx, by, nX, nY, true);
        const endAlignedHostRoofId = isShedHalfEdge ? undefined : findEdgeAlignedRoofId(ax, ay, bx, by, nX, nY, false);
        let height = baseHeight + rightHeightOffset + (rightSpan - distR) * firstRightSlope;
        const isRake = Math.abs(rightDist) > DIST_EPSILON && !isShedHalfEdge;
        // extension edges need to be classified as INVALID so the roofing material extrusion is also extended
        let side = RoofSide.RIGHT;
        const resolvedMaterialTrimAndProps = resolvedRoofMaterialTrimAndProps(
          polyLine2Edge(exactPerimeter, i),
          resolvedEdgeOverride,
          roofSettings,
          isRake,
          side,
          false,
        );
        const resolvedFramingType = resolveRoofWallFramingType(roofFramingSettings, resolvedEdgeOverride, side);

        if (isShedHalfEdge) {
          side = hasLeftExtension ? RoofSide.INVALID : RoofSide.LEFT;
          height = baseHeight + rightHeightOffset + (rightSpan + distR) * lastRightSlope;
        }

        exactEdges.push(
          roofEdgeNew({
            subRoofId,
            edge: polyLine2Edge(exactPerimeter, i),
            outerEdge: polyLine2Edge(offsetExactPerimeter, i),
            startHeight: height,
            endHeight: height,
            heightOffset: rightHeightOffset,
            slopes: roofType === GableRoofType.LEFT_SIDE_SHED_ROOF ? [] : rightSideSlopes,
            oppSlopes: roofType === GableRoofType.DEFAULT ? leftSideSlopes : [],
            eaveSlope:
              roofType === GableRoofType.LEFT_SIDE_SHED_ROOF || Math.abs(leftDist) < DIST_EPSILON
                ? Number.POSITIVE_INFINITY
                : eaveSlope,
            eaveExtensionAtCoplanarWall,
            eaveExtensionAtNonCoplanarRake,
            eaveType,
            eaveCutType: resolvedEdgeOverride.eaveCutType ?? eaveCutType,
            exteriorWallOffsetWithFinish: edgeExteriorWallOffsetWithFinish[i],
            exteriorWallOffsetFromOverhangDatum: edgeExteriorWallOffsetFromOverhangDatum[i],
            sideOverhang: edgeOverhangs[i],
            startOverhang: edgeOverhangs[(i + l - 1) % l],
            endOverhang: edgeOverhangs[(i + 1) % l],
            side,
            isLeftEdge: Math.abs(leftDist) < DIST_EPSILON,
            isRightEdge: Math.abs(rightDist) < DIST_EPSILON,
            isRake,
            startAlignedHostRoofId,
            endAlignedHostRoofId,
            roofMaterialTrimAndProps: resolvedMaterialTrimAndProps,
            roofEdgeOverrides,
            isShedHalfEdge,
            framingType: resolvedFramingType,
          }),
        );
      } else {
        const resolvedMaterialTrimAndProps = resolvedRoofMaterialTrimAndProps(
          polyLine2Edge(exactPerimeter, i),
          resolvedEdgeOverride,
          roofSettings,
          true,
          RoofSide.INVALID,
          false,
        );
        const resolvedFramingType = resolveRoofWallFramingType(
          roofFramingSettings,
          resolvedEdgeOverride,
          RoofSide.INVALID,
        );

        exactEdges.push(
          roofEdgeNew({
            subRoofId,
            edge: polyLine2Edge(exactPerimeter, i),
            outerEdge: polyLine2Edge(offsetExactPerimeter, i),
            startHeight: baseHeight,
            endHeight: baseHeight,
            side: RoofSide.INVALID,
            exteriorWallOffsetWithFinish: edgeExteriorWallOffsetWithFinish[i],
            exteriorWallOffsetFromOverhangDatum: edgeExteriorWallOffsetFromOverhangDatum[i],
            sideOverhang: edgeOverhangs[i],
            startOverhang: edgeOverhangs[(i + l - 1) % l],
            endOverhang: edgeOverhangs[(i + 1) % l],
            eaveType,
            eaveCutType: resolvedEdgeOverride.eaveCutType ?? eaveCutType,
            eaveExtensionAtCoplanarWall,
            eaveExtensionAtNonCoplanarRake,
            isRake: true,
            roofMaterialTrimAndProps: resolvedMaterialTrimAndProps,
            roofEdgeOverrides,
            framingType: resolvedFramingType,
          }),
        );
      }
    } else {
      const isStartEdge = isRoofEdgeAlignedToSide({
        boundingPerimeter,
        exactPerimeter,
        startEdge,
        side: RoofSide.START,
        edgeIndex: i,
      });

      const isEndEdge = isRoofEdgeAlignedToSide({
        boundingPerimeter,
        exactPerimeter,
        startEdge,
        side: RoofSide.END,
        edgeIndex: i,
      });

      let side = RoofSide.INVALID;
      let fullyTruncated = false;
      if (isStartEdge) {
        side = RoofSide.START;
        fullyTruncated = fullyTruncatedStart;
      } else if (isEndEdge) {
        side = RoofSide.END;
        fullyTruncated = fullyTruncatedEnd;
      }

      const resolvedMaterialTrimAndProps = resolvedRoofMaterialTrimAndProps(
        polyLine2Edge(exactPerimeter, i),
        resolvedEdgeOverride,
        roofSettings,
        false,
        side,
        fullyTruncated,
      );

      const resolvedFramingType = resolveRoofWallFramingType(roofFramingSettings, resolvedEdgeOverride, side);

      if (isStartEdge && fullyTruncatedStart) {
        exactEdges.push(
          roofEdgeNew({
            subRoofId,
            edge: polyLine2Edge(exactPerimeter, i),
            outerEdge: polyLine2Edge(offsetExactPerimeter, i),
            startHeight: baseHeight,
            endHeight: baseHeight,
            eaveSlope,
            slopes: startSlopes,
            eaveType,
            eaveCutType: resolvedEdgeOverride.eaveCutType ?? eaveCutType,
            exteriorWallOffsetWithFinish: edgeExteriorWallOffsetWithFinish[i],
            exteriorWallOffsetFromOverhangDatum: edgeExteriorWallOffsetFromOverhangDatum[i],
            sideOverhang: edgeOverhangs[i],
            startOverhang: edgeOverhangs[(i + l - 1) % l],
            endOverhang: edgeOverhangs[(i + 1) % l],
            isStartEdge: true,
            isRake: false,
            fullyTruncated: true,
            eaveExtensionAtCoplanarWall,
            eaveExtensionAtNonCoplanarRake,
            side: RoofSide.START,
            roofMaterialTrimAndProps: resolvedMaterialTrimAndProps,
            roofEdgeOverrides,
            framingType: resolvedFramingType,
          }),
        );
      } else if (isEndEdge && fullyTruncatedEnd) {
        exactEdges.push(
          roofEdgeNew({
            subRoofId,
            edge: polyLine2Edge(exactPerimeter, i),
            outerEdge: polyLine2Edge(offsetExactPerimeter, i),
            startHeight: baseHeight,
            endHeight: baseHeight,
            eaveSlope,
            slopes: endSlopes,
            eaveType,
            eaveCutType: resolvedEdgeOverride.eaveCutType ?? eaveCutType,
            exteriorWallOffsetWithFinish: edgeExteriorWallOffsetWithFinish[i],
            exteriorWallOffsetFromOverhangDatum: edgeExteriorWallOffsetFromOverhangDatum[i],
            sideOverhang: edgeOverhangs[i],
            startOverhang: edgeOverhangs[(i + l - 1) % l],
            endOverhang: edgeOverhangs[(i + 1) % l],
            isEndEdge: true,
            isRake: false,
            fullyTruncated: true,
            eaveExtensionAtCoplanarWall,
            eaveExtensionAtNonCoplanarRake,
            side: RoofSide.END,
            roofMaterialTrimAndProps: resolvedMaterialTrimAndProps,
            roofEdgeOverrides,
            framingType: resolvedFramingType,
          }),
        );
      } else {
        const side = line2Side(p0x, p0y, p1x, p1y, ax, ay);
        let gableSide = RoofSide.INVALID;

        let truncatedSlopes: RoofSlope[] = [];
        if (isStartEdge) {
          truncatedSlopes = startSlopes;

          // extension edges need to be classified as INVALID so the roofing material extrusion is also extended
          gableSide = hasStartExtension ? RoofSide.INVALID : RoofSide.START;
        } else if (isEndEdge) {
          truncatedSlopes = endSlopes;

          // extension edges need to be classified as INVALID so the roofing material extrusion is also extended
          gableSide = hasEndExtension ? RoofSide.INVALID : RoofSide.END;
        } else {
          gableSide = side === Line2Side.LEFT ? RoofSide.LEFT : RoofSide.RIGHT;
        }

        const resolvedFramingType = resolveRoofWallFramingType(roofFramingSettings, resolvedEdgeOverride, gableSide);

        const validEdgeForCustomHip = isEdgeValidForCustomHip(exactPerimeter, i) && !isStartEdge && !isEndEdge;

        const isCustomHipEdge = validEdgeForCustomHip && customHipSlope !== Number.POSITIVE_INFINITY;

        const resolvedMaterialTrimAndProps = resolvedRoofMaterialTrimAndProps(
          polyLine2Edge(exactPerimeter, i),
          resolvedEdgeOverride,
          roofSettings,
          true,
          gableSide,
          false,
        );

        let [startHeight, endHeight] = [baseHeight, baseHeight];

        if (!isCustomHipEdge) {
          [startHeight, endHeight] = getStartEndHeight(ax, ay, bx, by);
        }

        exactEdges.push(
          roofEdgeNew({
            subRoofId,
            edge: polyLine2Edge(exactPerimeter, i),
            outerEdge: polyLine2Edge(offsetExactPerimeter, i),
            startHeight,
            endHeight,
            isCustomHipEdge,
            truncatedSlopes,
            validEdgeForCustomHip,
            eaveSlope: validEdgeForCustomHip ? customHipSlope : Number.POSITIVE_INFINITY,
            eaveType,
            eaveCutType: resolvedEdgeOverride.eaveCutType ?? eaveCutType,
            exteriorWallOffsetWithFinish: edgeExteriorWallOffsetWithFinish[i],
            exteriorWallOffsetFromOverhangDatum: edgeExteriorWallOffsetFromOverhangDatum[i],
            sideOverhang: edgeOverhangs[i],
            startOverhang: edgeOverhangs[(i + l - 1) % l],
            endOverhang: edgeOverhangs[(i + 1) % l],
            isStartEdge,
            isEndEdge,
            isRake: true,
            eaveExtensionAtCoplanarWall,
            eaveExtensionAtNonCoplanarRake,
            side: gableSide,
            roofMaterialTrimAndProps: resolvedMaterialTrimAndProps,
            roofEdgeOverrides,
            framingType: resolvedFramingType,
          }),
        );
      }
    }
  }

  return exactEdges;
}

export function getRoofEdgeFromSide(
  roofVolume: Pick<GeneratedRoofVolumeLegacy, "edges">,
  side: RoofSide,
): RoofEdge | undefined {
  const { edges } = roofVolume;

  const edge = edges.find((e) => e.side === side);

  return edge;
}

export function fixRoofSideOverrides(
  roofSideOverrides: EvaluatedRoofSideOverridesTable,
  mirror: boolean,
  peakDirection: RoofPeakDirection | undefined,
): EvaluatedRoofSideOverridesTable {
  let finalRoofSideOverrides: EvaluatedRoofSideOverridesTable = {};

  if (!roofSideOverrides) {
    return finalRoofSideOverrides;
  }

  // filter out undefined values
  for (const side in roofSideOverrides) {
    const roofSideOverride = roofSideOverrides[side];
    if (!roofSideOverride) {
      continue;
    }

    const filteredRoofSideOverride: RoofEdgeOverrides = {};
    for (const id in roofSideOverride) {
      const val = roofSideOverride[id];

      if (val !== undefined && val !== null) {
        filteredRoofSideOverride[id] = val;
      }
    }

    finalRoofSideOverrides[side] = filteredRoofSideOverride;
  }

  // if there is no mirror, then we don't need to do anything
  if (!mirror) {
    return finalRoofSideOverrides;
  }

  if (peakDirection !== undefined && isIndexedRoofPeakDirection[peakDirection]) {
    // flip start/end side overrides
    finalRoofSideOverrides = {
      ...finalRoofSideOverrides,
      [RoofSide.START]: finalRoofSideOverrides[RoofSide.END],
      [RoofSide.END]: finalRoofSideOverrides[RoofSide.START],
    };
  } else {
    // flip left/right side overrides
    finalRoofSideOverrides = {
      ...finalRoofSideOverrides,
      [RoofSide.LEFT]: finalRoofSideOverrides[RoofSide.RIGHT],
      [RoofSide.RIGHT]: finalRoofSideOverrides[RoofSide.LEFT],
    };
  }

  return finalRoofSideOverrides;
}

export function reverseEdgeOverrides(
  roofEdgeOverrides: EvaluatedRoofEdgeOverridesTable,
  edgeCount: number,
): EvaluatedRoofEdgeOverridesTable {
  const newRoofEdgeOverrides: EvaluatedRoofEdgeOverridesTable = {};
  for (const edgeIndex in roofEdgeOverrides) {
    const i = parseInt(edgeIndex);

    newRoofEdgeOverrides[edgeCount - 1 - i] = roofEdgeOverrides[edgeIndex];
  }

  return newRoofEdgeOverrides;
}

export function shiftEdgeOverrides(
  roofEdgeOverrides: EvaluatedRoofEdgeOverridesTable,
  edgeCount: number,
  shift: number,
): EvaluatedRoofEdgeOverridesTable {
  const newRoofEdgeOverrides: EvaluatedRoofEdgeOverridesTable = {};
  for (const edgeIndex in roofEdgeOverrides) {
    const i = parseInt(edgeIndex);

    newRoofEdgeOverrides[(i + shift) % edgeCount] = roofEdgeOverrides[edgeIndex];
  }

  return newRoofEdgeOverrides;
}

export function fixRoofEdgeOverrides(
  roofEdgeOverrides: EvaluatedRoofEdgeOverridesTable,
  edgeCount: number,
  roofSettings: RoofSettings,
  roofBlocks: RoofBlocks,
  mirror: boolean,
): EvaluatedRoofEdgeOverridesTable {
  const filteredRoofEndOverrides: EvaluatedRoofEdgeOverridesTable = {};

  // roof edge overrides only apply for clip to perimeter free form roofs
  if (!isValidRoofTypeForEdgeOverrides(roofSettings, roofBlocks)) {
    return filteredRoofEndOverrides;
  }

  // filter out undefined values
  for (const edgeId in roofEdgeOverrides) {
    const roofEdgeOverride = roofEdgeOverrides[edgeId];
    if (!roofEdgeOverride) {
      continue;
    }

    const filteredRoofEdgeOverride: EvaluatedRoofEdgeOverrides = {};
    for (const id in roofEdgeOverride) {
      const val = roofEdgeOverride[id];

      if (val !== undefined && val !== null) {
        filteredRoofEdgeOverride[id] = val;
      }
    }

    filteredRoofEndOverrides[edgeId] = filteredRoofEdgeOverride;
  }

  // if mirror is true, then we need to reverse the overrides
  if (!roofEdgeOverrides || !mirror) {
    return filteredRoofEndOverrides;
  }

  let newRoofEdgeOverrides = reverseEdgeOverrides(filteredRoofEndOverrides, edgeCount);
  newRoofEdgeOverrides = shiftEdgeOverrides(newRoofEdgeOverrides, edgeCount, 1);

  return newRoofEdgeOverrides;
}

function isEdgeValidForCustomHip(exactPerimeter: PolyLine2, edgeIndex: number): boolean {
  const [ax, ay] = polyLine2EdgeStart(exactPerimeter, edgeIndex - 1);
  const [bx, by] = polyLine2EdgeStart(exactPerimeter, edgeIndex);
  const [cx, cy] = polyLine2EdgeEnd(exactPerimeter, edgeIndex);
  const [dx, dy] = polyLine2EdgeEnd(exactPerimeter, edgeIndex + 1);

  const startDir = line2Side(ax, ay, bx, by, cx, cy, DIST_EPSILON);
  const endDir = line2Side(bx, by, cx, cy, dx, dy, DIST_EPSILON);

  const isStartOutsideCorner = startDir === Line2Side.LEFT;
  const isEndOutsideCorner = endDir === Line2Side.LEFT;

  // TODO: improve this detection -> Valid edges are the ones that
  // have the rest of the edges on its back side

  return isStartOutsideCorner && isEndOutsideCorner;
}

export function isRoofEdgeValidForCustomHipEdge(
  roofVolume: Pick<GeneratedRoofVolumeLegacy, "exactEdges">,
  edgeIndex: number,
): boolean {
  if (!roofVolume) {
    return false;
  }

  const { exactEdges } = roofVolume;
  if (edgeIndex >= exactEdges.length) {
    return false;
  }

  const { validEdgeForCustomHip } = exactEdges[edgeIndex];

  return !!validEdgeForCustomHip;
}

export function getRoofEdgeAlignedSide(
  roofVolume: Pick<GeneratedRoofVolumeLegacy, "blocks" | "perimeters">,
  edgeIndex: number,
): RoofSide {
  if (!roofVolume || !isVirtualRoofBlock(roofVolume.blocks)) {
    return RoofSide.INVALID;
  }

  const { blocks, perimeters } = roofVolume;
  const { startEdge, boundingPerimeter } = perimeters;
  const exactPerimeter = blocks.perimeter;

  const roofSides = [RoofSide.START, RoofSide.END, RoofSide.LEFT, RoofSide.RIGHT];
  for (const side of roofSides) {
    if (isRoofEdgeAlignedToSide({ boundingPerimeter, exactPerimeter, startEdge, side, edgeIndex })) {
      return side;
    }
  }

  return RoofSide.INVALID;
}

export function isRoofSideEdge(
  roofVolume: Pick<GeneratedRoofVolumeLegacy, "blocks" | "perimeters">,
  edgeIndex: number,
): boolean {
  if (!roofVolume || !isVirtualRoofBlock(roofVolume.blocks)) {
    return false;
  }

  const { blocks, perimeters } = roofVolume;
  const { startEdge, boundingPerimeter } = perimeters;
  const exactPerimeter = blocks.perimeter;

  const isLeftAligned = isRoofEdgeAlignedToSide({
    boundingPerimeter,
    exactPerimeter,
    startEdge,
    side: RoofSide.LEFT,
    edgeIndex,
  });

  const isRightAligned = isRoofEdgeAlignedToSide({
    boundingPerimeter,
    exactPerimeter,
    startEdge,
    side: RoofSide.RIGHT,
    edgeIndex,
  });

  return isLeftAligned || isRightAligned;
}

export function isRoofStartEdge(
  roofVolume: Pick<GeneratedRoofVolumeLegacy, "blocks" | "perimeters">,
  edgeIndex: number,
): boolean {
  if (!roofVolume || !isVirtualRoofBlock(roofVolume.blocks)) {
    return false;
  }

  const { blocks, perimeters } = roofVolume;
  const { startEdge, boundingPerimeter } = perimeters;
  const exactPerimeter = blocks.perimeter;

  return isRoofEdgeAlignedToSide({
    boundingPerimeter,
    exactPerimeter,
    startEdge,
    side: RoofSide.START,
    edgeIndex,
  });
}

export function isRoofEndEdge(
  roofVolume: Pick<GeneratedRoofVolumeLegacy, "blocks" | "perimeters">,
  edgeIndex: number,
): boolean {
  if (!roofVolume || !isVirtualRoofBlock(roofVolume.blocks)) {
    return false;
  }

  const { blocks, perimeters } = roofVolume;
  const { startEdge, boundingPerimeter } = perimeters;
  const exactPerimeter = blocks.perimeter;

  return isRoofEdgeAlignedToSide({
    boundingPerimeter,
    exactPerimeter,
    startEdge,
    side: RoofSide.END,
    edgeIndex,
  });
}

export function isRoofEdgeStartOrEnd(
  roofVolume: Pick<GeneratedRoofVolumeLegacy, "blocks" | "perimeters">,
  edgeIndex: number,
): boolean {
  return isRoofStartEdge(roofVolume, edgeIndex) || isRoofEndEdge(roofVolume, edgeIndex);
}

export function isRoofEdgeRake(
  roofVolume: Pick<GeneratedRoofVolumeLegacy, "blocks" | "exactEdges">,
  edgeIndex: number,
): boolean {
  if (!roofVolume) {
    return false;
  }

  const { exactEdges } = roofVolume;

  if (edgeIndex >= exactEdges.length) {
    return false;
  }

  return !!exactEdges[edgeIndex]?.isRake;
}

export function isRoofEdgeFullyTruncated(
  roofVolume: Pick<GeneratedRoofVolumeLegacy, "blocks" | "exactEdges">,
  edgeIndex: number,
): boolean {
  if (!roofVolume) {
    return false;
  }

  const { exactEdges } = roofVolume;

  if (edgeIndex >= exactEdges.length) {
    return false;
  }

  return !!exactEdges[edgeIndex]?.fullyTruncated;
}

export function isRoofSideRake(roofVolume: Pick<GeneratedRoofVolumeLegacy, "edges">, side: RoofSide): boolean {
  if (!roofVolume) {
    return false;
  }
  const edge = getRoofEdgeFromSide(roofVolume, side);

  return !!edge?.isRake;
}

export function isRoofSideFullyTruncated(
  roofVolume: Pick<GeneratedRoofVolumeLegacy, "edges">,
  side: RoofSide,
): boolean {
  if (!roofVolume) {
    return false;
  }
  const edge = getRoofEdgeFromSide(roofVolume, side);

  return !!edge?.fullyTruncated;
}

export function isRoofEdgeAligningWithStreetOrientation(
  roofVolume: Pick<GeneratedRoofVolumeLegacy, "blocks" | "perimeters">,
  edgeIndex: number,
  orientation: StreetOrientationMode,
): boolean {
  if (!roofVolume || !isVirtualRoofBlock(roofVolume.blocks)) {
    return false;
  }

  const {
    blocks: { perimeter: exactPerimeter },
  } = roofVolume;

  const edge = polyLine2Edge(exactPerimeter, edgeIndex);
  const [startX, startY] = polyLine2Start(edge);
  const [dirX, dirY] = polyLine2EdgeTangent(edge, 0);
  const edgePlane = planeNew(startX, startY, 0, dirX, dirY, 0, 0, 0, 1);

  return includeElementBasedOnOrientation(edgePlane, orientation);
}

export function isRoofSideAligningWithStreetOrientation(
  roofVolume: Pick<GeneratedRoofVolumeLegacy, "blocks" | "perimeters">,
  side: RoofSide,
  orientation: StreetOrientationMode,
): boolean {
  if (!roofVolume) {
    return false;
  }

  const { perimeters } = roofVolume;
  const { startEdge, boundingPerimeter } = perimeters;

  const basicEdgeCount = polyLine2EdgeCount(boundingPerimeter);

  let edge: PolyLine2 | undefined;
  switch (side) {
    case RoofSide.START:
      edge = polyLine2Edge(boundingPerimeter, startEdge);
      break;
    case RoofSide.END:
      edge = polyLine2Edge(boundingPerimeter, (startEdge + 2) % basicEdgeCount);
      break;
    case RoofSide.LEFT:
      edge = polyLine2Edge(boundingPerimeter, (startEdge + 3) % basicEdgeCount);
      break;
    case RoofSide.RIGHT:
      edge = polyLine2Edge(boundingPerimeter, (startEdge + 1) % basicEdgeCount);
      break;
    case RoofSide.INVALID:
      break;
    default:
      assertUnreachable(side);
  }

  if (!edge) {
    return false;
  }

  const [startX, startY] = polyLine2Start(edge);
  const [dirX, dirY] = polyLine2EdgeTangent(edge, 0);
  const edgePlane = planeNew(startX, startY, 0, dirX, dirY, 0, 0, 0, 1);

  return includeElementBasedOnOrientation(edgePlane, orientation);
}

export function getSideFromRoofEdge(
  roofVolume: Pick<GeneratedRoofVolumeLegacy, "blocks" | "perimeters">,
  edgeIndex: number,
): RoofSide {
  if (!roofVolume || !isVirtualRoofBlock(roofVolume.blocks)) {
    return RoofSide.INVALID;
  }

  const { blocks, perimeters } = roofVolume;
  const { startEdge, boundingPerimeter } = perimeters;
  const exactPerimeter = blocks.perimeter;

  if (isRoofEdgeAlignedToSide({ boundingPerimeter, exactPerimeter, startEdge, side: RoofSide.START, edgeIndex })) {
    return RoofSide.START;
  } else if (isRoofEdgeAlignedToSide({ boundingPerimeter, exactPerimeter, startEdge, side: RoofSide.END, edgeIndex })) {
    return RoofSide.END;
  } else if (
    isRoofEdgeAlignedToSide({ boundingPerimeter, exactPerimeter, startEdge, side: RoofSide.LEFT, edgeIndex })
  ) {
    return RoofSide.LEFT;
  } else if (
    isRoofEdgeAlignedToSide({ boundingPerimeter, exactPerimeter, startEdge, side: RoofSide.RIGHT, edgeIndex })
  ) {
    return RoofSide.RIGHT;
  }

  return RoofSide.INVALID;
}

export function getRoofEdgeHandlesFromSide(roofVolume: GeneratedRoofVolumeLegacy, side: RoofSide): RoofEdgeHandle[] {
  if (!roofVolume || !isVirtualRoofBlock(roofVolume.blocks)) {
    return [];
  }

  const { blocks, perimeters, id } = roofVolume;
  const { startEdge, boundingPerimeter } = perimeters;
  const exactPerimeter = blocks.perimeter;

  const roofHandle: RoofHandle = { type: "ROOF", id };
  const edgeHandles: RoofEdgeHandle[] = [];

  for (let i = 0, l = polyLine2EdgeCount(exactPerimeter); i < l; i++) {
    if (isRoofEdgeAlignedToSide({ boundingPerimeter, exactPerimeter, startEdge, side, edgeIndex: i })) {
      const edgeHandle: RoofEdgeHandle = {
        type: "EDGE",
        parent: roofHandle,
        shapeId: "outline",
        edgeId: i,
      };

      edgeHandles.push(edgeHandle);
    }
  }

  return edgeHandles;
}

export function buildRoofRails2D(leftRail: PolyLine2, rightRail: PolyLine2): RoofRails2D {
  const peakRail = polyLine2Morph(leftRail, rightRail, 0.5);
  const peakRailNoExtension = polyLine2Copy(peakRail);
  const startRail = polyLine2ByTwoPoints(leftRail[0], leftRail[1], rightRail[0], rightRail[1]);
  const endRail = polyLine2ByTwoPoints(leftRail[2], leftRail[3], rightRail[2], rightRail[3]);

  return roofRails2DNew(leftRail, rightRail, startRail, endRail, peakRail, peakRailNoExtension);
}

export function mirrorRoofWallSide(
  wallSide: RoofSide,
  mirror: boolean,
  peakDirection: RoofPeakDirection | undefined,
): RoofSide {
  if (mirror) {
    switch (wallSide) {
      case RoofSide.LEFT:
        return RoofSide.RIGHT;
      case RoofSide.RIGHT:
        return RoofSide.LEFT;
      case RoofSide.START:
        return peakDirection === RoofPeakDirection.BY_LOW_RAIL_INDEX ? RoofSide.END : RoofSide.START;
      case RoofSide.END:
        return peakDirection === RoofPeakDirection.BY_LOW_RAIL_INDEX ? RoofSide.START : RoofSide.END;
      default:
        return wallSide;
    }
  } else {
    return wallSide;
  }
}

export function getRoofSideWallFramingType(edges: RoofEdge[], side: RoofSide): RoofWallFramingType {
  const edge = edges.find((e) => e.side === side);

  return edge?.framingType ?? RoofWallFramingType.AUTO;
}
